/**
 * DocumentModalFooter — bottom action bar of DocumentModal.
 *
 * Extracted from DocumentModal.tsx to reduce render complexity.
 * Purely presentational: all state and handlers are passed as props.
 *
 * Contains:
 * - QuickAddTaskButton (hidden in Smart Finance mobile shell)
 * - Delete button (with confirmation handled by parent's onDeleteClick)
 * - Mobile side panel toggle
 * - Close button
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Trash2, Sparkles, Brain, GraduationCap, ChevronDown, Ban } from 'lucide-react';
import { type DocumentConfig, type DocumentFormState } from '../documentModalHelpers';
import { type Document, type DocumentType } from '@/hooks/useDocumentsWorkItems';
import QuickAddTaskButton from '@/components/shared/QuickAddTaskButton';
import SidePanelToggle, { type SidePanelMode } from '../SidePanelToggle';

export interface DocumentModalFooterProps {
  document: Document | null | undefined;
  doc_type: DocumentType;
  config: DocumentConfig;
  formData: DocumentFormState;
  isDarkMode: boolean;
  isCompactMobileDoc: boolean;
  financeMobileCompactShell: boolean;
  presentationMode: 'default' | 'smarttask-mobile' | 'finance-mobile';
  permissions: {
    can_view?: boolean;
    can_create?: boolean;
    can_edit?: boolean;
    can_delete?: boolean;
    can_admin?: boolean;
  };
  onDelete: ((id: string) => void) | undefined;
  onClose: () => void;
  handleCloseModal: () => void;
  onDeleteClick: () => Promise<void>;
  onVoidClick: () => Promise<void>;
  canVoid: boolean;
  sidePanelMode: SidePanelMode;
  setSidePanelMode: React.Dispatch<React.SetStateAction<SidePanelMode>>;
  shouldShowContextualAssistant: boolean;
}

export function DocumentModalFooter({
  document,
  doc_type,
  config,
  isDarkMode,
  isCompactMobileDoc,
  financeMobileCompactShell,
  presentationMode,
  permissions,
  onDelete,
  onClose: _onClose,
  handleCloseModal,
  onDeleteClick,
  onVoidClick,
  canVoid,
  sidePanelMode,
  setSidePanelMode,
  shouldShowContextualAssistant,
}: DocumentModalFooterProps) {
  const { primaryColor } = useTheme();
  const [showAiMenu, setShowAiMenu] = useState(false);
  const aiMenuRef = useRef<HTMLDivElement>(null);
  const showContextualAssistant = sidePanelMode === 'ai';

  // Close the AI sub-menu when clicking outside
  useEffect(() => {
    if (!showAiMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (aiMenuRef.current && !aiMenuRef.current.contains(e.target as Node)) {
        setShowAiMenu(false);
      }
    };
    window.document.addEventListener('mousedown', handleClickOutside);
    return () => window.document.removeEventListener('mousedown', handleClickOutside);
  }, [showAiMenu]);

  return (
    <div
      className={`border-t flex-shrink-0 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} ${financeMobileCompactShell
        ? 'flex flex-row items-center gap-2 px-3 py-2'
        : isCompactMobileDoc
          ? 'px-3 py-3 flex flex-col gap-3'
          : 'px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between'
        }`}
      style={isCompactMobileDoc ? { paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' } : undefined}
    >
      {!financeMobileCompactShell && (
        <div className={`flex items-center gap-2 ${isCompactMobileDoc ? 'justify-between' : ''}`}>
          {/* Add task — hidden in Smart Finance mobile shell */}
          {document?.id && presentationMode !== 'finance-mobile' && (
            <QuickAddTaskButton
              variant="icon"
              title={`${config.labels.title}: ${document.doc_number || document.item_number || 'New'}`}
              referenceType="document"
              referenceId={String(document.id)}
              referenceUrl={`/client-zone/modules/total-finance?doc=${document.id}&type=${doc_type}`}
              contextData={{
                doc_type: doc_type,
                doc_number: document.doc_number || document.item_number,
                status: document.status,
                grand_total: document.grand_total,
                contact_name: document.contact_name
              }}
              priority="medium"
              size="md"
            />
          )}
          {/* Delete — keep on desktop / SmartTask; omit in Smart Finance compact footer */}
          {document?.id && permissions.can_delete && onDelete && !financeMobileCompactShell && (
            <button
              onClick={onDeleteClick}
              className={`px-2 sm:px-3 py-2 text-red-600 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 text-sm ${isDarkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'}`}
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">Delete</span>
            </button>
          )}
          {/* Void — red button to mark the document as void (terminal status) */}
          {document?.id && canVoid && !financeMobileCompactShell && (
            <button
              onClick={onVoidClick}
              className={`px-2 sm:px-3 py-2 text-red-700 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 text-sm font-medium border border-red-300 dark:border-red-700 ${isDarkMode ? 'hover:bg-red-900/30 bg-red-900/10' : 'hover:bg-red-100 bg-red-50'}`}
              title="Mark this document as void"
            >
              <Ban className="w-4 h-4" />
              <span className="hidden sm:inline">Void</span>
            </button>
          )}
        </div>
      )}
      <div className={`flex items-center gap-2 ${financeMobileCompactShell || isCompactMobileDoc ? 'w-full flex-1' : ''}`}>
        {/* Mobile side panel toggle (lg:hidden since desktop uses header toggle).
            AI is hidden here — it's triggered from the AI sub-menu button below. */}
        <div className="lg:hidden">
          <SidePanelToggle
            mode={sidePanelMode}
            onChange={(mode) => setSidePanelMode(mode)}
            isDark={isDarkMode}
            hideAI={true}
          />
        </div>

        {/* AI sub-menu — presents AI Assistant and AI Training Coach options.
            The assistant opens the contextual AI Finance Assistant side panel
            (existing behavior via sidePanelMode='ai').
            The training coach dispatches 'ai-coach-open' (picked up by the
            GuidedTour mounted at the bottom of DocumentModal). */}
        {shouldShowContextualAssistant && (
          <div ref={aiMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setShowAiMenu((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${showContextualAssistant
                ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                : 'text-white shadow-sm hover:opacity-90'
                }`}
              style={showContextualAssistant ? undefined : { backgroundColor: primaryColor }}
              title="AI Assistant"
              aria-expanded={showAiMenu}
              aria-haspopup="menu"
            >
              <Sparkles className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">AI Assistant</span>
              <span className="sm:hidden">AI</span>
              <ChevronDown className={`h-3 w-3 transition-transform ${showAiMenu ? 'rotate-180' : ''}`} />
            </button>
            {showAiMenu && (
              <div
                role="menu"
                className="absolute bottom-full right-0 mb-2 w-64 rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800"
              >
                <button
                  role="menuitem"
                  onClick={() => {
                    setShowAiMenu(false);
                    if (showContextualAssistant) {
                      setSidePanelMode('none');
                    } else {
                      setSidePanelMode('ai');
                      // Notify the coach to close (mutual exclusivity)
                      window.dispatchEvent(new CustomEvent('ai-assistant-open'));
                    }
                  }}
                  className="flex w-full items-start gap-3 rounded-t-lg px-3 py-2.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50"
                >
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-500 text-white">
                    <Brain className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold text-slate-800 dark:text-slate-100">AI Assistant</span>
                    <span className="block text-[10px] text-slate-500 dark:text-slate-400">Ask questions about this document</span>
                  </span>
                </button>
                <button
                  role="menuitem"
                  onClick={() => {
                    setShowAiMenu(false);
                    // Only dismiss the AI assistant panel — leave photos/details
                    // untouched. The coach renders in a portal overlay and does
                    // not need the side panel closed.
                    setSidePanelMode((prev) => (prev === 'ai' ? 'none' : prev));
                    window.dispatchEvent(new CustomEvent('ai-coach-open'));
                  }}
                  className="flex w-full items-start gap-3 rounded-b-lg px-3 py-2.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50"
                >
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-red-500 text-white">
                    <GraduationCap className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold text-slate-800 dark:text-slate-100">AI Training Coach</span>
                    <span className="block text-[10px] text-slate-500 dark:text-slate-400">Guided, step-by-step tour of this document</span>
                  </span>
                </button>
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleCloseModal}
          className={`border rounded-lg transition-colors text-sm ${isDarkMode ? 'border-gray-600 hover:bg-gray-700 text-gray-200' : 'border-gray-300 hover:bg-gray-100 text-gray-700'} ${isCompactMobileDoc || financeMobileCompactShell ? 'w-full py-3 font-medium' : 'px-3 sm:px-4 py-2'}`}
        >
          Close
        </button>
      </div>
    </div>
  );
}

export default DocumentModalFooter;
